import { getDisplayText, type IncomeRecord } from "@aera/data-table";
import type { LiffFinancialWorkCard } from "@/lib/financialWork/types";
import { displayWorkTitle } from "@/lib/financialWork/nav";
import { incomeRecordSourceLabel } from "@/lib/financialWork/incomeRecordSourceLabel";

function readIncomeAmountMajor(
  record: IncomeRecord | Record<string, unknown>,
  ...keys: string[]
): number | null {
  const raw = record as Record<string, unknown>;
  for (const key of keys) {
    const text = getDisplayText(raw[key]).trim();
    if (!text) continue;
    const n = Number(text);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readIncomeFieldText(
  record: IncomeRecord | Record<string, unknown>,
  ...keys: string[]
): string {
  const raw = record as Record<string, unknown>;
  for (const key of keys) {
    const text = getDisplayText(raw[key]).trim();
    if (text) return text;
  }
  return "";
}

export function incomeAmountMajorToMinor(major: number | null | undefined): number {
  const n = Number(major);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

export function readIncomeTransferMinor(record: IncomeRecord): number {
  return incomeAmountMajorToMinor(
    readIncomeAmountMajor(record, "transfer_amount", "total_amount"),
  );
}

export function readIncomeAllocatedMinor(record: IncomeRecord): number {
  const allocatedMajor = readIncomeAmountMajor(
    record,
    "allocated_amount",
    "slip_matched_amount",
  );
  if (allocatedMajor != null && allocatedMajor >= 0) {
    return incomeAmountMajorToMinor(allocatedMajor);
  }
  return readIncomeTransferMinor(record);
}

function readRecordField(record: IncomeRecord, ...keys: string[]): string {
  const raw = record as Record<string, unknown>;
  for (const key of keys) {
    const value = getDisplayText(raw[key]).trim();
    if (value) return value;
  }
  return "";
}

const BANK_DISPLAY: Record<string, string> = {
  kbank: "KBank",
  kb: "KBank",
  scb: "SCB",
  bbl: "BBL",
  ktb: "KTB",
  tmb: "TTB",
  ttb: "TTB",
  bay: "BAY",
  gsb: "GSB",
};

function formatBankLabel(raw: string): string {
  const value = raw.trim();
  if (!value) return "";
  const key = value.toLowerCase().replace(/[\s_-]+/g, "");
  return BANK_DISPLAY[key] ?? value;
}

export function financialWorkIncomeRecordIdFromCard(
  card: LiffFinancialWorkCard,
): string | null {
  const fromContext = card.contextRows.find(
    (row) => row.key === "income_record",
  )?.value?.trim();
  if (fromContext) return fromContext;
  const nav = card.availableActions.find(
    (action) => action.actionType === "open_data",
  )?.dataNav;
  if (nav?.workerId?.trim() !== "bill-lin-income") return null;
  return nav.recordId?.trim() || null;
}

function formatQueueAmount(raw: number): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  return n.toLocaleString("th-TH", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function incomeRecordQueueTitle(record: IncomeRecord): string {
  const party = readRecordField(
    record,
    "party_name",
    "sender_name",
    "customer_name",
  );
  const rawAmount = readIncomeAmountMajor(
    record,
    "transfer_amount",
    "total_amount",
  );
  const amount =
    rawAmount != null ? formatQueueAmount(rawAmount) : "";
  if (party && amount) return `${party} จ่าย ${amount} บาท`;
  if (party) return party;
  return amount ? `จ่าย ${amount} บาท` : "—";
}

const MANUAL_MATCH_LABEL = "จับคู่กับยอดค้างที่เลือก";
const AUTO_MATCH_LABEL = "จับคู่ยอดอัตโนมัติ";

function normalizeQueueMatchLabel(raw: string): string {
  const text = raw.trim();
  if (!text || text === "—") return AUTO_MATCH_LABEL;
  if (
    /จับคู่กับยอดค้างที่เลือก|จับคู่เอง|manual|เลือกยอด|ยืนยันเอง/i.test(text)
  ) {
    return MANUAL_MATCH_LABEL;
  }
  if (
    /ปิดยอด|จับคู่ยอดเดียวอัตโนมัติ|จับคู่ยอดอัตโนมัติ|อัตโนมัติ/i.test(text)
  ) {
    return AUTO_MATCH_LABEL;
  }
  if (/จับคู่/i.test(text)) return MANUAL_MATCH_LABEL;
  return text;
}

function formatBankChannelLabel(raw: string): string {
  const bank = formatBankLabel(raw);
  if (!bank) return "";
  const upper = bank.toUpperCase();
  if (upper === "KBANK" || upper === "KB") return "KBANK";
  return bank;
}

function matchLabelFromMethod(method: string): string | null {
  switch (method.trim()) {
    case "manual":
      return MANUAL_MATCH_LABEL;
    case "auto_amount":
    case "auto_balance":
      return AUTO_MATCH_LABEL;
    default:
      return null;
  }
}

function matchMethodCodeFromLabel(label: string): string {
  if (label === MANUAL_MATCH_LABEL || label === "จับคู่เอง") return "manual";
  return "auto_amount";
}

function isSourceLikeLine(text: string): boolean {
  return /^(?:ยอดค้าง|สลิป(?:จาก)? LINE|เงินโอนจาก LINE|รายการธนาคาร|รายจ่าย)\s*[·•-]/i.test(
    text.trim(),
  );
}

function partyFromSourceLine(source: string): string | undefined {
  const trimmed = source.trim();
  const dotted = trimmed.match(/[·•]\s*(.+)$/);
  if (dotted?.[1]?.trim()) return dotted[1].trim();
  const dashed = trimmed.match(/(?:ยอดค้าง|สลิป(?:จาก)? LINE)\s*-\s*(.+)$/i);
  return dashed?.[1]?.trim() || undefined;
}

function partyFromPayTitle(title: string): string | undefined {
  const text = displayWorkTitle(title).trim();
  const matched = text.match(/^(.+?)\s+จ่าย\s+[\d,]+(?:\.\d+)?\s*บาท?/u);
  return matched?.[1]?.trim() || undefined;
}

function cardContextValue(
  card: LiffFinancialWorkCard,
  key: string,
): string {
  return card.contextRows.find((row) => row.key === key)?.value?.trim() || "";
}

function lastDecisionIsUserConfirmed(raw: string): boolean {
  const value = raw.trim().toLowerCase();
  if (!value) return false;
  // Exact token — leftover `select` / slip_owner_select is the ask state, not a match method.
  return (
    value === "user_confirmed" ||
    /(^|[_\s.])user_confirmed($|[_\s.])/.test(value)
  );
}

function subtitleLooksLikeMatchLabel(subtitle: string): boolean {
  return /จับคู่|ปิดยอด|อัตโนมัติ|manual|auto_amount|auto_balance/i.test(
    subtitle,
  );
}

function inferClosedMatchLabel(args: {
  subtitle?: string;
  source?: string;
  kind?: string;
  lastDecision?: string;
  matchMethod?: string;
}): string {
  if ((args.kind || "").toLowerCase() === "expense") return AUTO_MATCH_LABEL;

  const fromMethod = args.matchMethod
    ? matchLabelFromMethod(args.matchMethod)
    : null;
  if (fromMethod) return fromMethod;

  const lastDecision = args.lastDecision?.trim() || "";
  if (lastDecisionIsUserConfirmed(lastDecision)) return MANUAL_MATCH_LABEL;

  const subtitle = args.subtitle?.trim() || "";
  if (
    subtitle &&
    !isSourceLikeLine(subtitle) &&
    subtitleLooksLikeMatchLabel(subtitle)
  ) {
    return normalizeQueueMatchLabel(subtitle);
  }

  return AUTO_MATCH_LABEL;
}

function isReceivableSourceKind(kind?: string, source = ""): boolean {
  const k = (kind || "").toLowerCase();
  return (
    k === "receivable" ||
    k === "receivable_batch" ||
    /ยอดค้าง/i.test(source)
  );
}

function channelLabelForOrigin(
  origin: "line" | "system",
  receivable: boolean,
): string {
  if (origin === "line") {
    return receivable ? "ยอดค้างจาก LINE" : "สลิปจาก LINE";
  }
  return receivable ? "ยอดค้างจาก ระบบ" : "สลิปจาก ระบบ";
}

function inferClosedSourceChannel(args: {
  source?: string;
  kind?: string;
  title?: string;
  subtitle?: string;
  sourceExtra?: string;
  taxInvoice?: string;
  documentType?: string;
}): string {
  const source = args.source?.trim() || "";
  const kind = (args.kind || "").toLowerCase();
  if (kind === "expense") {
    return expenseQueueSourceChannel({
      title: args.title,
      subtitle: args.subtitle,
      source,
      sourceExtra: args.sourceExtra || source,
      taxInvoice: args.taxInvoice,
      documentType: args.documentType || args.sourceExtra,
    });
  }
  const receivable = isReceivableSourceKind(args.kind, source);

  if (/^ยอดค้างจาก LINE$|^สลิปจาก LINE$|^สลิปมาจาก LINE$/i.test(source)) {
    return channelLabelForOrigin("line", receivable || /ยอดค้าง/.test(source));
  }
  if (/^ยอดค้างจาก ระบบ$|^สลิปจาก ระบบ$/i.test(source)) {
    return channelLabelForOrigin("system", receivable || /ยอดค้าง/.test(source));
  }
  if (/สลิป.*LINE|LINE.*สลิป|เงินโอนจาก LINE|สลิปมาจาก LINE|สลิปจาก LINE/i.test(source)) {
    return channelLabelForOrigin("line", receivable);
  }
  if (/รายการธนาคาร|ธนาคาร/i.test(source) && !/LINE|สลิป/i.test(source)) {
    return "บัญชีธนาคาร";
  }
  if (args.kind === "payment") {
    if (/ระบบ/i.test(source)) return channelLabelForOrigin("system", false);
    return channelLabelForOrigin("line", false);
  }
  if (/LINE/i.test(source)) return channelLabelForOrigin("line", receivable);
  if (/ยอดค้าง|ระบบ/i.test(source)) {
    return channelLabelForOrigin("system", receivable);
  }
  if (
    /^รายจ่าย$|^(ใบเสร็จ|ใบแจ้งหนี้|Invoice) จาก (ระบบ|LINE|ไลน์|เว็บ|OCR|รับสลิป)$/i.test(
      source,
    )
  ) {
    return expenseQueueSourceChannel({
      title: args.title,
      subtitle: args.subtitle,
      source,
      sourceExtra: args.sourceExtra,
      taxInvoice: args.taxInvoice,
      documentType: args.documentType || args.sourceExtra,
    });
  }
  if (source && !isSourceLikeLine(source) && !/^(ระบบ|LINE)$/i.test(source)) {
    return source;
  }
  return channelLabelForOrigin("system", receivable);
}

/** Same short channel label as the ปิดล่าสุด queue row (e.g. สลิปจาก LINE). */
export function feedItemProvenanceSourceChannel(item: {
  sourceLabel?: string;
  kind?: string;
  title?: string;
  subtitle?: string;
  documentType?: string;
}): string {
  return inferClosedSourceChannel({
    source: item.sourceLabel,
    kind: item.kind,
    title: item.title,
    subtitle: item.subtitle,
    sourceExtra: item.documentType || item.sourceLabel,
    documentType: item.documentType,
  });
}

function isExpenseInvoiceDocument(args: {
  title?: string;
  subtitle?: string;
  source?: string;
  sourceExtra?: string;
  taxInvoice?: string;
}): boolean {
  const hay = [
    args.title,
    args.subtitle,
    args.source,
    args.sourceExtra,
    args.taxInvoice,
  ]
    .filter(Boolean)
    .join(" ");
  if (/invoice|ใบแจ้งหนี้|ใบกำกับ|ยังจ่ายไม่ได้|ยังไม่จ่าย/i.test(hay)) {
    return true;
  }
  const tax = (args.taxInvoice || "").trim();
  if (tax && !/^(ไม่มี|no|false|0|-|n\/a)$/i.test(tax)) {
    return true;
  }
  return false;
}

function expenseIngressOrigin(args: {
  title?: string;
  subtitle?: string;
  source?: string;
  sourceExtra?: string;
}): string {
  const source = (args.source || "").trim();
  const extra = (args.sourceExtra || "").trim();
  const hay = [source, extra, args.title, args.subtitle].filter(Boolean).join(" ");

  const fromLabel = hay.match(
    /(?:ใบเสร็จ|ใบแจ้งหนี้|Invoice)\s*จาก\s*(LINE|ไลน์|เว็บ|ระบบ|OCR|รับสลิป)/i,
  );
  if (fromLabel) {
    const origin = fromLabel[1] || "";
    if (/LINE|ไลน์/i.test(origin)) return "LINE";
    if (/เว็บ/i.test(origin)) return "เว็บ";
    if (/OCR/i.test(origin)) return "OCR";
    if (/รับสลิป/i.test(origin)) return "รับสลิป";
    if (/ระบบ/i.test(origin)) return "ระบบ";
  }

  if (/^(LINE|ไลน์|line_oa)$/i.test(source) || /^(LINE|ไลน์|line_oa)$/i.test(extra)) {
    return "LINE";
  }
  if (/\bLINE\b|ไลน์|line_oa/i.test(hay)) return "LINE";
  if (/เว็บ|\bweb\b/i.test(hay)) return "เว็บ";
  if (/\bOCR\b/i.test(hay)) return "OCR";
  if (/รับสลิป/i.test(hay)) return "รับสลิป";
  return "ระบบ";
}

function expenseQueueSourceChannel(args: {
  title?: string;
  subtitle?: string;
  source?: string;
  sourceExtra?: string;
  taxInvoice?: string;
  documentType?: string;
}): string {
  const doc = (args.documentType || "").trim().toLowerCase();
  const extra = (args.sourceExtra || "").trim();
  const origin = expenseIngressOrigin(args);

  let kind: "invoice" | "receipt" | null = null;
  if (doc === "invoice" || doc === "ใบแจ้งหนี้") kind = "invoice";
  else if (doc === "receipt" || doc === "ใบเสร็จ") kind = "receipt";
  else if (
    /^(ใบแจ้งหนี้|Invoice)\s*จาก/i.test(args.source || "") ||
    /^(ใบแจ้งหนี้|Invoice)\s*จาก/i.test(extra) ||
    (/invoice|ใบแจ้งหนี้|ใบกำกับ/i.test(extra) &&
      !/^(รายจ่าย|ระบบ|LINE|ไลน์)$/i.test(extra))
  ) {
    kind = "invoice";
  } else if (
    /^ใบเสร็จ\s*จาก/i.test(args.source || "") ||
    /^ใบเสร็จ\s*จาก/i.test(extra) ||
    (/ใบเสร็จ/i.test(extra) && !/^(รายจ่าย|ระบบ|LINE|ไลน์)$/i.test(extra))
  ) {
    kind = "receipt";
  } else if (isExpenseInvoiceDocument(args)) {
    kind = "invoice";
  } else {
    kind = "receipt";
  }

  return kind === "invoice" ? `ใบแจ้งหนี้จาก ${origin}` : `ใบเสร็จจาก ${origin}`;
}

/** Provenance + detail header source — mirrors ปิดล่าสุด, not raw feed subtitles. */
export function closedProvenanceSourceLabel(
  card: LiffFinancialWorkCard,
  record?: IncomeRecord | null,
  _evidenceItems?: LiffFinancialWorkCard["evidenceItems"],
): string {
  if (record) {
    return incomeRecordQueueSourceChannel(record);
  }
  const kind =
    cardContextValue(card, "kind") ||
    String(card.objectRef?.kind || "").trim();
  if (kind === "expense") {
    return expenseQueueSourceChannel({
      title: card.title,
      subtitle: card.subtitle,
      source: cardContextValue(card, "source"),
      sourceExtra: cardContextValue(card, "source_extra"),
      taxInvoice: cardContextValue(card, "tax_invoice"),
      documentType: cardContextValue(card, "document_type"),
    });
  }
  return closedSourceChannelFromCard(card);
}

function inferClosedByLabel(args: {
  title: string;
  source?: string;
  kind?: string;
  matchedCustomer?: string;
}): string | undefined {
  const matched = args.matchedCustomer?.trim();
  if (matched) return matched;

  if (args.kind === "receivable") {
    return partyFromSourceLine(args.source || "") || partyFromPayTitle(args.title);
  }

  return partyFromPayTitle(args.title) || partyFromSourceLine(args.source || "");
}

export function closedFeedCardHints(item: {
  title: string;
  subtitle?: string;
  sourceLabel?: string;
  kind?: string;
  lastDecision?: string | null;
  matchMethod?: string | null;
}): {
  matchLabel: string;
  sourceLabel: string;
  closedByLabel?: string;
  matchMethod: string;
} {
  const source = item.sourceLabel?.trim() || "";
  const matchLabel = inferClosedMatchLabel({
    subtitle: item.subtitle,
    source,
    kind: item.kind,
    lastDecision: item.lastDecision || "",
    matchMethod: item.matchMethod || "",
  });
  return {
    matchLabel,
    sourceLabel: inferClosedSourceChannel({
      source,
      kind: item.kind,
      title: item.title,
      subtitle: item.subtitle,
    }),
    closedByLabel: inferClosedByLabel({
      title: item.title,
      source,
      kind: item.kind,
    }),
    matchMethod: matchMethodCodeFromLabel(matchLabel),
  };
}

export function incomeRecordQueueMatchLabel(
  record: IncomeRecord,
  card?: LiffFinancialWorkCard | null,
): string {
  const fromMethod = matchLabelFromMethod(
    readRecordField(record, "match_method"),
  );
  if (fromMethod) return fromMethod;

  const status = readRecordField(record, "status");
  const jobType = readRecordField(record, "job_type", "money_type");
  const remark = readRecordField(record, "remark", "extra_info");
  const hay = `${status} ${jobType} ${remark}`;

  if (
    /จับคู่กับยอดค้างที่เลือก|จับคู่เอง|manual|เลือกยอด|user_confirmed|ยืนยัน/i.test(
      hay,
    )
  ) {
    return MANUAL_MATCH_LABEL;
  }
  if (/ปิดยอด|ปิดงาน|อัตโนมัติ|auto/i.test(hay)) return AUTO_MATCH_LABEL;

  const hasSlip = Boolean(String(record.image_url ?? "").trim());
  const hasBank = Boolean(
    readRecordField(record, "sender_bank", "source_extra"),
  );
  if (hasSlip || hasBank) return AUTO_MATCH_LABEL;

  const subtitle = card?.subtitle?.trim() || "";
  return normalizeQueueMatchLabel(subtitle);
}

export function incomeRecordQueueSourceChannel(
  record: IncomeRecord,
): string {
  const ingress = readRecordField(record, "ingress_channel", "channel").toLowerCase();
  if (ingress === "system") return "สลิปจาก ระบบ";

  const hasSlip = Boolean(String(record.image_url ?? "").trim());
  const bankRaw = readRecordField(record, "sender_bank", "source_extra");
  const bank = formatBankChannelLabel(bankRaw);
  const userId = readRecordField(record, "user_id", "income_user_id");

  if (hasSlip || ingress === "line" || ingress === "line_oa" || userId.startsWith("line_")) {
    return "สลิปจาก LINE";
  }
  if (bank) return `บัญชี ${bank}`;
  if (/รายการธนาคาร|ธนาคาร/i.test(incomeRecordSourceLabel(record))) {
    return bankRaw
      ? `บัญชี ${formatBankChannelLabel(bankRaw) || "ธนาคาร"}`
      : "บัญชีธนาคาร";
  }

  const full = incomeRecordSourceLabel(record);
  if (/LINE/i.test(full)) return "สลิปจาก LINE";
  if (/ระบบ/i.test(full)) return "สลิปจาก ระบบ";
  if (bank) return `บัญชี ${bank}`;
  return "สลิปจาก ระบบ";
}

export function incomeRecordQueueClosedByLabel(
  record: IncomeRecord,
): string | undefined {
  const matched = readRecordField(record, "matched_receivable_customer");
  if (matched) return matched;
  const customer = readRecordField(record, "customer_name", "recipient_name");
  if (customer) return customer;
  return undefined;
}

export function incomeRecordQueueSourceShort(record: IncomeRecord): string {
  return incomeRecordQueueSourceChannel(record);
}

export function incomeRecordQueueUpdatedAt(record: IncomeRecord): string {
  return readIncomeFieldText(
    record,
    "created_at",
    "recorded_at",
    "updated_at",
  );
}

export function incomeRecordQueueOccurredAt(record: IncomeRecord): string {
  return readIncomeFieldText(
    record,
    "created_at",
    "record_date",
    "recorded_at",
    "transfer_datetime",
  );
}

function formatBahtMinor(minor: number): string {
  const major = minor / 100;
  const rounded = Math.round(major);
  const abs = Math.abs(rounded).toLocaleString("th-TH");
  return rounded < 0 ? `−฿${abs}` : `฿${abs}`;
}

function readMinorFromCard(card: LiffFinancialWorkCard, key: string): number {
  const raw = card.contextRows.find((row) => row.key === key)?.value;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function isExpenseQueueCard(card: LiffFinancialWorkCard): boolean {
  const workerId = card.availableActions.find((action) => action.dataNav?.workerId)
    ?.dataNav?.workerId;
  if (workerId === "bill-lin-expense" || card.objectRef.kind === "expense") {
    return true;
  }
  const kind = card.contextRows.find((row) => row.key === "kind")?.value ?? "";
  const haystack = `${card.title} ${card.subtitle ?? ""} ${kind}`;
  return /ใบเสร็จ|รายจ่าย|เงินออก|ตลาดสด|เงินสดหน้าร้าน/.test(haystack);
}

/** เงินเข้า and เงินออกจริง are mutually exclusive — income vs expense column. */
export function queueMoneyColumnsFromMinors(
  transferMinor: number,
  allocatedMinor: number,
  expense: boolean,
): { moneyIn: string | null; moneyOut: string | null } {
  if (expense) {
    const outMinor = allocatedMinor > 0 ? allocatedMinor : transferMinor;
    return {
      moneyIn: null,
      moneyOut: outMinor > 0 ? formatBahtMinor(outMinor) : null,
    };
  }
  return {
    moneyIn: transferMinor > 0 ? formatBahtMinor(transferMinor) : null,
    moneyOut: null,
  };
}

function closedMoneyFromCard(card: LiffFinancialWorkCard): {
  moneyIn: string | null;
  moneyOut: string | null;
  related: string;
} {
  let transferMinor = card.amountSummary?.primaryAmountMinor ?? 0;
  if (transferMinor <= 0) {
    transferMinor = readMinorFromCard(card, "transfer_amount");
  }
  let allocatedMinor = readMinorFromCard(card, "allocated_amount");
  if (allocatedMinor <= 0 && transferMinor > 0) {
    allocatedMinor = transferMinor;
  }
  const expense = isExpenseQueueCard(card);
  const { moneyIn, moneyOut } = queueMoneyColumnsFromMinors(
    transferMinor,
    allocatedMinor,
    expense,
  );
  const relatedMinor = expense
    ? allocatedMinor > 0
      ? allocatedMinor
      : transferMinor
    : transferMinor;
  return {
    moneyIn,
    moneyOut,
    related: relatedMinor > 0 ? formatBahtMinor(relatedMinor) : "—",
  };
}

export type ClosedQueueRowDisplay = {
  title: string;
  matchLabel: string;
  sourceLabel: string;
  closedByLabel?: string;
  occurredAt: string;
  fromIncome: boolean;
  moneyIn: string | null;
  moneyOut: string | null;
  related: string;
  statusTitle: string;
  statusDetail?: string;
};

function closedTitleFromCard(card: LiffFinancialWorkCard): string {
  const title = displayWorkTitle(card.title).trim();
  if (/จ่าย/.test(title)) return title;
  const matched = title.match(/^(.+?)\s+([\d,]+(?:\.\d+)?)\s*บาท$/u);
  if (matched) return `${matched[1].trim()} จ่าย ${matched[2]} บาท`;
  return title;
}

function closedMatchLabelFromCard(card: LiffFinancialWorkCard): string {
  const kind =
    cardContextValue(card, "kind") ||
    String(card.objectRef?.kind || "").trim();
  if (kind.toLowerCase() === "expense" || isExpenseQueueCard(card)) {
    return AUTO_MATCH_LABEL;
  }

  const fromContext = cardContextValue(card, "match_method");
  const fromMethod = fromContext ? matchLabelFromMethod(fromContext) : null;
  if (fromMethod) return fromMethod;

  return inferClosedMatchLabel({
    subtitle: card.subtitle,
    source: cardContextValue(card, "source"),
    kind,
    lastDecision: cardContextValue(card, "last_decision"),
    matchMethod: fromContext,
  });
}

function closedSourceChannelFromCard(card: LiffFinancialWorkCard): string {
  const stored = cardContextValue(card, "source");
  const kind =
    cardContextValue(card, "kind") ||
    String(card.objectRef?.kind || "").trim();
  if (/^บัญชี /.test(stored)) return stored;
  if (
    /^สลิปจาก LINE$|^สลิปมาจาก LINE$|^ยอดค้างจาก LINE$|^สลิปจาก ระบบ$|^ยอดค้างจาก ระบบ$|^ระบบ$|^รายจ่าย$|^(ใบเสร็จ|ใบแจ้งหนี้|Invoice) จาก (ระบบ|LINE|ไลน์|เว็บ|OCR|รับสลิป)$|^LINE$|^ไลน์$/.test(
      stored,
    )
  ) {
    return inferClosedSourceChannel({
      source: stored,
      kind,
      title: card.title,
      subtitle: card.subtitle,
      sourceExtra: cardContextValue(card, "source_extra"),
      taxInvoice: cardContextValue(card, "tax_invoice"),
    });
  }
  return inferClosedSourceChannel({
    source: stored || card.timelineEvents?.[0]?.label?.trim() || "",
    kind,
    title: card.title,
    subtitle: card.subtitle,
    sourceExtra: cardContextValue(card, "source_extra"),
    taxInvoice: cardContextValue(card, "tax_invoice"),
  });
}

function closedByLabelFromCard(card: LiffFinancialWorkCard): string | undefined {
  return inferClosedByLabel({
    title: card.title,
    source: cardContextValue(card, "source"),
    kind: cardContextValue(card, "kind"),
    matchedCustomer: cardContextValue(card, "matched_receivable_customer"),
  });
}

export function describeClosedQueueRow(
  card: LiffFinancialWorkCard,
  record?: IncomeRecord | null,
): ClosedQueueRowDisplay {
  const incomeRecordId = card.contextRows.find(
    (row) => row.key === "income_record",
  )?.value?.trim();
  const money = closedMoneyFromCard(card);
  const statusTitle = "ปิดยอดแล้ว";

  if (record) {
    const matchLabel = incomeRecordQueueMatchLabel(record, card);
    const transferMinor = readIncomeTransferMinor(record);
    const allocatedMinor = readIncomeAllocatedMinor(record);
    const closedByLabel = incomeRecordQueueClosedByLabel(record);
    const { moneyIn, moneyOut } = queueMoneyColumnsFromMinors(
      transferMinor,
      allocatedMinor,
      false,
    );
    return {
      title: incomeRecordQueueTitle(record),
      matchLabel,
      sourceLabel: incomeRecordQueueSourceChannel(record),
      closedByLabel,
      occurredAt: incomeRecordQueueUpdatedAt(record),
      fromIncome: true,
      moneyIn,
      moneyOut,
      related: transferMinor > 0 ? formatBahtMinor(transferMinor) : "—",
      statusTitle,
      statusDetail: closedByLabel,
    };
  }

  if (incomeRecordId) {
    const occurredAt =
      card.contextRows.find((row) => row.key === "created_at")?.value?.trim() ||
      card.contextRows.find((row) => row.key === "occurred_at")?.value?.trim() ||
      card.timelineSummary?.occurredAt ||
      card.timelineEvents?.[0]?.occurredAtLabel ||
      card.contextRows.find((row) => row.key === "updated_at")?.value?.trim() ||
      "";
    const matchLabel = closedMatchLabelFromCard(card);
    const closedByLabel = closedByLabelFromCard(card);
    return {
      title: closedTitleFromCard(card),
      matchLabel,
      sourceLabel: closedSourceChannelFromCard(card),
      closedByLabel,
      occurredAt,
      fromIncome: true,
      ...money,
      statusTitle,
      statusDetail: closedByLabel,
    };
  }

  const matchLabel = closedMatchLabelFromCard(card);
  const closedByLabel = closedByLabelFromCard(card);

  return {
    title: closedTitleFromCard(card),
    matchLabel,
    sourceLabel: closedSourceChannelFromCard(card),
    closedByLabel,
    occurredAt:
      card.contextRows.find((row) => row.key === "created_at")?.value ||
      card.contextRows.find((row) => row.key === "occurred_at")?.value ||
      card.timelineSummary?.occurredAt ||
      card.timelineEvents?.[0]?.occurredAtLabel ||
      card.contextRows.find((row) => row.key === "updated_at")?.value ||
      "",
    fromIncome: false,
    ...money,
    statusTitle,
    statusDetail: closedByLabel,
  };
}
